'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { caveat } from 'utils/fonts';
import { VOUCHER_MESSAGE_MAX } from 'content/gifting/voucherConfig';
import { voucherAssetUrl } from 'components/gifting/voucher/assets';
import { usePrefersReducedMotion } from 'hooks/media/usePrefersReducedMotion';
import { TrashIcon } from './TrashIcon';
import styles from './NoteCard.module.css';

type Props = {
  id: string;
  value: string;
  onChange: (v: string) => void;
};

const PEEL_MS = 960;

/** Paper note: "Surprise me" types a suggestion out, the trash corner peels the sheet away. */
export const NoteCard = ({ id, value, onChange }: Props) => {
  const t = useTranslations('gifting');
  const calm = usePrefersReducedMotion();
  const notes = t.raw('voucher.customizer.who.notes') as string[];
  const cardRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const caretRef = useRef<HTMLElement>(null);
  const timer = useRef(0);
  const peelTimer = useRef(0);
  const last = useRef(-1);
  const [peeling, setPeeling] = useState(false);
  const peelingRef = useRef(false);

  const stop = () => {
    window.clearTimeout(timer.current);
    timer.current = 0;
  };
  useEffect(
    () => () => {
      stop();
      window.clearTimeout(peelTimer.current);
    },
    [],
  );

  const surprise = () => {
    if (peelingRef.current) return;
    let k: number;
    do {
      k = Math.floor(Math.random() * notes.length);
    } while (k === last.current && notes.length > 1);
    last.current = k;
    const text = notes[k].slice(0, VOUCHER_MESSAGE_MAX);
    stop();
    if (calm) {
      onChange(text);
      return;
    }
    let i = 0;
    const step = () => {
      i++;
      onChange(text.slice(0, i));
      if (i >= text.length) {
        timer.current = 0;
        return;
      }
      const ch = text.charAt(i - 1);
      let d = 34 + Math.random() * 38;
      if (ch === ',') d += 140;
      else if (ch === '.' || ch === '!' || ch === '?') d += 260;
      timer.current = window.setTimeout(step, d);
    };
    onChange('');
    timer.current = window.setTimeout(step, 0);
  };

  // Peel/reset/fresh classes are toggled on the DOM directly: the swap must land
  // in one frame with transitions off, which a React re-render can't guarantee.
  const peelAway = () => {
    const card = cardRef.current;
    const ta = taRef.current;
    const caret = caretRef.current;
    if (peelingRef.current || !value.length || !card || !ta || !caret) return;
    stop();
    if (calm) {
      onChange('');
      return;
    }
    peelingRef.current = true;
    setPeeling(true);
    card.classList.add(styles.isPeel);
    peelTimer.current = window.setTimeout(() => {
      onChange('');
      ta.classList.add(styles.isFresh);
      caret.classList.add(styles.isFresh);
      card.classList.add(styles.isReset);
      card.classList.remove(styles.isPeel);
      void card.offsetWidth;
      card.classList.remove(styles.isReset);
      peelingRef.current = false;
      setPeeling(false);
      ta.focus({ preventScroll: true });
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          ta.classList.add(styles.isFreshIn);
          caret.classList.add(styles.isFreshIn);
          peelTimer.current = window.setTimeout(() => {
            ta.classList.remove(styles.isFresh, styles.isFreshIn);
            caret.classList.remove(styles.isFresh, styles.isFreshIn);
          }, 1100);
        }),
      );
    }, PEEL_MS);
  };

  // A letter typed while nothing has focus starts the note, if the card is on
  // screen. Focused controls, Space (page scroll) and shortcuts are left alone.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      const el = document.activeElement;
      if (el && el !== document.body && el !== document.documentElement) return;
      if (e.key.length !== 1 || e.key === ' ') return;
      const card = cardRef.current;
      if (!card || peelingRef.current) return;
      const r = card.getBoundingClientRect();
      if (r.bottom < 80 || r.top > window.innerHeight - 80) return;
      stop();
      taRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      className={`${styles.letterWrap} ${caveat.variable}`}
      style={
        {
          '--grain': `url(${voucherAssetUrl('paper-grain.png')})`,
        } as React.CSSProperties
      }
    >
      <div ref={cardRef} className={styles.card}>
        <textarea
          ref={taRef}
          id={id}
          className={styles.note}
          value={value}
          maxLength={VOUCHER_MESSAGE_MAX}
          placeholder={t('voucher.customizer.who.messagePlaceholder')}
          onKeyDown={stop}
          onChange={(e) => {
            stop();
            onChange(e.target.value);
          }}
        />
        <i ref={caretRef} className={styles.caret} aria-hidden="true" />
        <div className={styles.peel} aria-hidden="true">
          <i className={styles.cut}>
            <s />
          </i>
          <i className={styles.fold}>
            <b />
          </i>
        </div>
        <button
          type="button"
          className={styles.del}
          aria-label={t('voucher.customizer.who.clearMessage')}
          disabled={!value.length || peeling}
          onClick={peelAway}
        >
          <TrashIcon className={styles.trash} />
        </button>
      </div>
      <div className={styles.noteChips}>
        <button type="button" className={styles.chip} onClick={surprise}>
          {t('voucher.customizer.who.surprise')}
        </button>
      </div>
      <span className={styles.count}>
        {t('voucher.customizer.who.count', {
          count: value.length,
          max: VOUCHER_MESSAGE_MAX,
        })}
      </span>
    </div>
  );
};
